/**
 * API Service Layer Foundation
 *
 * This module serves as the centralized gateway for ALL external API communications
 * in the MDL Fan platform. It implements critical architectural patterns that ensure
 * security, consistency, and maintainability across the entire application.
 *
 * Key architectural decisions:
 * - Centralized service layer pattern (vs direct fetch calls in components)
 *   WHY: Single source of truth for authentication, error handling, and request/response transformations
 * - Token injection at service level (vs component-level auth handling)
 *   WHY: Prevents token leakage, ensures consistent auth across all API calls
 * - localStorage for token storage (vs sessionStorage or cookies)
 *   WHY: Persists across tabs and browser sessions for seamless multi-tab experience
 *
 * Security considerations:
 * - JWT tokens automatically injected into ALL authenticated requests
 * - Bearer token pattern follows OAuth 2.0 specifications
 * - Token presence validated but expiry handled by backend (401 responses)
 *   WHY: Client-side JWT validation can be bypassed; backend is source of truth
 *
 * Integration patterns:
 * - Works with React Query for caching and state management
 * - Compatible with AWS Cognito's token refresh mechanism
 * - Supports multi-tenant architecture via API_URL from constants.js
 *
 * Performance optimizations:
 * - JSON.stringify called once for body serialization
 * - Headers merged efficiently to prevent object recreation
 * - Axios used for built-in request/response interceptors and retry capabilities
 *
 * @module apiFetch
 * @see {@link .claude/api-integration-rules.md} - API integration patterns and best practices
 * @see {@link src/utils/constants.js} - API_URL configuration (CloudFront → API Gateway)
 * @see {@link src/features/authentication/useAuth.js} - Token lifecycle management
 */

import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { API_URL } from "../utils/constants";

// WHY module-level: Compiled once, reused across all error paths.
// Matches terse infrastructure responses from API Gateway / WAF that are
// not user-friendly. Longer business messages pass through untouched.
const INFRA_PATTERNS = /^(route not found|not found|forbidden|missing authentication token)\.?$/i;

/**
 * HTTP methods supported by the API
 */
type HttpMethod = "get" | "post" | "put" | "patch" | "delete";

/**
 * Request parameters for getData function
 */
interface GetDataParams {
  /**
   * HTTP method (default: 'get')
   */
  method?: HttpMethod;

  /**
   * API endpoint path (relative to API_URL)
   * @example '/user/profile'
   * @example '/content/upload'
   */
  path: string;

  /**
   * Request body for POST/PUT/PATCH requests (default: {})
   */
  body?: Record<string, unknown>;

  /**
   * Additional headers to merge with defaults (default: {})
   */
  headers?: Record<string, string>;
}

/**
 * Request parameters for postData function
 */
interface PostDataParams {
  /**
   * API endpoint path
   * @example '/auth/register'
   * @example '/content/create'
   */
  path: string;

  /**
   * Request body data (automatically JSON stringified)
   */
  data?: Record<string, unknown>;

  /**
   * Optional headers (merged with defaults)
   */
  headers?: Record<string, string>;
}

/**
 * Request parameters for deleteData function
 */
interface DeleteDataParams {
  /**
   * API endpoint path
   * @example '/content/:id'
   * @example '/subscription/:id'
   */
  path: string;

  /**
   * Optional request body (e.g., deletion metadata)
   */
  data?: Record<string, unknown>;

  /**
   * Optional headers (merged with defaults)
   */
  headers?: Record<string, string>;
}

/**
 * Core API request handler implementing the service layer pattern.
 *
 * This function serves as the foundation for ALL API communications, providing:
 * - Automatic JWT token injection for authenticated requests
 * - Consistent header management across all API calls
 * - Centralized error transformation for user-friendly messages
 * - Request/response logging hooks for debugging
 *
 * WHY this architecture:
 * - Single point of control for authentication changes (e.g., switching from JWT to OAuth)
 * - Prevents scattered fetch() calls that bypass security and error handling
 * - Enables global request/response transformations without touching feature code
 * - Facilitates testing through single mock point rather than multiple fetch mocks
 *
 * @param params - Request configuration
 * @returns Response data from the API (response.data extracted)
 * @throws {Error} Transformed error with user-friendly message from backend
 *
 * @example
 * // Authenticated GET request
 * const user = await getData({ path: '/user/profile' });
 *
 * @example
 * // POST with custom headers
 * const result = await getData({
 *   method: 'post',
 *   path: '/content/upload',
 *   body: { title: 'Match Highlight' },
 *   headers: { 'X-Channel-Id': 'bristolcity' }
 * });
 */
export async function getData<TResponse = unknown>({
  method = "get",
  path,
  body = {},
  headers = {},
}: GetDataParams): Promise<TResponse> {
  // WHY localStorage over sessionStorage: Persists across browser sessions and tabs,
  // enabling seamless multi-tab experience without re-authentication.
  // Security trade-off: Vulnerable to XSS, but mitigated by:
  // 1) HttpOnly cookies for refresh tokens (not accessible via JS)
  // 2) Short-lived access tokens (15 min expiry)
  // 3) Content Security Policy headers preventing script injection
  const token = localStorage.getItem("accessToken");

  const config: AxiosRequestConfig = {
    method,
    baseURL: API_URL, // WHY baseURL: CloudFront distribution provides geo-distributed caching and DDoS protection
    url: path,
    headers,
    data: JSON.stringify(body), // WHY pre-stringify: Axios would auto-stringify, but explicit control prevents double-encoding edge cases
  };

  // WHY merge headers in this order: User-provided headers override defaults,
  // but security headers (Authorization) must override user headers to prevent tampering
  config.headers = {
    ...headers, // User headers first (can be overridden)
    Accept: "application/json", // WHY explicit Accept: Some legacy endpoints return HTML on error without this
    "Content-type": "application/json", // WHY always JSON: API Gateway expects JSON; form-data handled by separate upload service
  };

  // WHY conditional token injection: Allows both authenticated and public endpoints
  // through same service layer. Public endpoints (channel config, public content)
  // don't require tokens and would fail with unnecessary auth headers.
  if (token) {
    config.headers = {
      Authorization: `Bearer ${token}`, // WHY Bearer scheme: Industry standard, AWS API Gateway expects this format
      ...config.headers, // WHY headers merged after: Prevents user from overriding Authorization header
    };
  }

  try {
    const res: AxiosResponse<TResponse> = await axios(config);

    // WHY return res.data instead of full response:
    // 1) Simplifies consumer code (no need for response.data everywhere)
    // 2) Headers rarely needed; when needed, can be added via response interceptor
    // 3) Consistent with REST convention where body contains the resource
    return res.data;
  } catch (err) {
    // WHY throw new Error with response.data:
    // Backend returns structured error messages designed for user display.
    // Creating new Error strips axios-specific properties that would confuse error boundaries.
    // React Query and error boundaries expect standard Error objects.
    const axiosError = err as AxiosError<Record<string, unknown>>;
    const responseData = axiosError.response?.data;

    // Extract error message from response body or use a safe generic default.
    // WHY: Backend endpoints use different shapes — auth returns { error: "..." },
    // other endpoints may return { message: "..." }. We try both.
    // axiosError.message is never used because it contains raw HTTP text like
    // "Request failed with status code 400" which is not user-friendly.
    const rawMessage =
      (responseData as any)?.error ||
      (responseData as any)?.message ||
      "";

    // WHY: Infrastructure errors (API Gateway 403/404, WAF blocks) return
    // terse messages like "Route not found", "Forbidden", "Missing Authentication Token"
    // that are not user-friendly. Replace these with a generic fallback.
    // We match only short, known infrastructure patterns — longer messages
    // with business context (e.g. "Subscription not found for this user.") pass through.
    const status = axiosError.response?.status;
    const isInfrastructureError =
      !rawMessage ||
      ((status === 403 || status === 404) && INFRA_PATTERNS.test(rawMessage.trim()));

    const errorMessage = isInfrastructureError
      ? "An unexpected error occurred. Please try again later."
      : rawMessage;

    const error = new Error(errorMessage);
    (error as any).statusCode = status;
    // WHY: Flag for errorHandling.ts to detect connectivity failures reliably.
    // String-matching the message is fragile — apiFetch may replace the original
    // axios message with a generic fallback, so the downstream isNetworkError()
    // check would never fire. A typed flag survives any message transformation.
    (error as any).isNetworkError = !axiosError.response;
    throw error;
  }
}

/**
 * POST request convenience wrapper.
 *
 * WHY this exists: Provides semantic clarity in consumer code and enforces
 * consistent naming conventions across the codebase. Instead of remembering
 * getData({method: 'post', body: data}), developers use intuitive postData({data}).
 *
 * Performance consideration: Zero overhead - just parameter remapping.
 * React Query integration: Used in mutations for create/update operations.
 *
 * @param params - Request parameters
 * @returns Response data from the API
 * @throws {Error} Transformed error with user-friendly message
 *
 * @example
 * // User registration
 * const user = await postData({
 *   path: '/auth/register',
 *   data: { email, password }
 * });
 *
 * @example
 * // Creating content with channel context
 * const content = await postData({
 *   path: '/content/create',
 *   data: { title, description },
 *   headers: { 'X-Channel-Id': channelId }
 * });
 */
export async function postData<TResponse = unknown>({
  path,
  data = {},
  headers = {},
}: PostDataParams): Promise<TResponse> {
  return getData<TResponse>({
    method: "post",
    path,
    body: data, // WHY renamed: 'body' is HTTP terminology, 'data' is developer-friendly
    headers,
  });
}

/**
 * DELETE request convenience wrapper.
 *
 * WHY this exists: DELETE operations often need special handling (confirmations,
 * cascade deletes, soft vs hard delete). Having a dedicated function allows
 * future enhancement without changing consumer code.
 *
 * WHY data parameter: While DELETE typically doesn't have a body, some APIs
 * require additional context (e.g., reason for deletion, cascade options).
 * API Gateway and modern REST APIs support DELETE with body.
 *
 * Security consideration: DELETE operations are irreversible. This wrapper
 * ensures proper authentication via getData's token injection, preventing
 * unauthorized deletions.
 *
 * @param params - Request parameters
 * @returns Response data from the API (usually confirmation)
 * @throws {Error} Transformed error with user-friendly message
 *
 * @example
 * // Simple resource deletion
 * await deleteData({ path: `/content/${contentId}` });
 *
 * @example
 * // Deletion with metadata
 * await deleteData({
 *   path: `/subscription/${subId}`,
 *   data: { reason: 'Customer requested cancellation', refund: true }
 * });
 */
export async function deleteData<TResponse = unknown>({
  path,
  data = {},
  headers = {},
}: DeleteDataParams): Promise<TResponse> {
  return getData<TResponse>({
    method: "delete",
    path,
    body: data,
    headers,
  });
}
